<template>
  <label class="app-radio" :class="{ 'app-radio--disabled': disabled, [`app-radio--${size}`]: true }">
    <input
      :id="inputId"
      ref="input"
      type="radio"
      v-bind="$attrs"
      :name="name"
      :value="nativeValue"
      :checked="isChecked"
      :disabled="disabled"
      :aria-invalid="!!error || null"
      class="app-radio__input"
      @change="onChange"
      @blur="$emit('blur', $event)"
      @focus="$emit('focus', $event)"
    />
    <span class="app-radio__bullet"></span>
    <span class="app-radio__label">
      <slot>
        <span v-if="label" v-html="label"></span>
      </slot>
    </span>
  </label>
</template>

<script>
let uid = 0

/**
 * App-styled radio button — no-Bootstrap, drop-in upgrade over `c-radio`.
 *
 * Same v-model contract: `value` is the currently-selected option (from the
 * group), `nativeValue` is what THIS radio represents. When clicked, emits
 * `input(nativeValue)`.
 *
 * Migration from c-radio:
 *   - `dataValue` → `nativeValue` (less confusing name)
 *   - `value` works the same (currently-selected)
 *   - Added `disabled`, `size`, `error`, slot for custom label.
 */
export default {
  name: 'AppRadio',

  inheritAttrs: false,

  props: {
    value:       { type: [String, Number, Boolean, null], default: null },
    nativeValue: { type: [String, Number, Boolean, null], default: null },
    label:       { type: String,  default: '' },
    name:        { type: String,  default: '' },
    disabled:    { type: Boolean, default: false },
    error:       { type: String,  default: '' },
    id:          { type: String,  default: '' },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md'].includes(v),
    },
  },

  emits: ['input', 'change', 'blur', 'focus'],

  data() {
    return {
      autoId: `app-radio-${++uid}`,
    }
  },

  computed: {
    inputId() { return this.id || this.autoId },

    isChecked() {
      // Loose equality on purpose — radio values often arrive as strings even
      // when the source is a number (HTML attribute coercion).
      // eslint-disable-next-line eqeqeq
      return this.value == this.nativeValue
    },
  },

  methods: {
    onChange() {
      this.$emit('input', this.nativeValue)
      this.$emit('change', this.nativeValue)
    },

    focus() { if (this.$refs.input) this.$refs.input.focus() },
  },
}
</script>

<style lang="scss" scoped>
$ar-text:        #2b3a4a;
$ar-bg-surface:  #ffffff;
$ar-bg-soft:     #f6f8fa;
$ar-border:      #cfd6dc;
$ar-border-soft: rgba(0, 0, 0, 0.10);
$ar-blue:        #1f73c1;
$ar-blue-focus:  #4392d4;
$ar-red:         #d04848;
$ar-duration:    140ms;
$ar-ease:        cubic-bezier(.4, 0, .2, 1);

.app-radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;

  &--disabled {
    cursor: not-allowed;
    pointer-events: none;
    opacity: 0.7;
  }

  &__input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 1px;
    height: 1px;
    margin: -1px;
  }

  &__bullet {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: $ar-bg-surface;
    border: 1px solid $ar-border;
    position: relative;
    transition:
      background $ar-duration $ar-ease,
      border-color $ar-duration $ar-ease,
      box-shadow $ar-duration $ar-ease;

    // Inner dot — fades in when checked.
    &::after {
      content: '';
      position: absolute;
      top: 50%; left: 50%;
      width: 8px; height: 8px;
      border-radius: 50%;
      background: #fff;
      transform: translate(-50%, -50%) scale(0);
      transition: transform $ar-duration $ar-ease;
    }
  }

  &__label {
    font-size: 13px;
    color: $ar-text;
    line-height: 1.4;
  }

  // ── Hover ──
  &:hover &__bullet {
    border-color: $ar-blue-focus;
  }

  // ── Checked ──
  &__input:checked + &__bullet {
    background: $ar-blue;
    border-color: $ar-blue;

    &::after { transform: translate(-50%, -50%) scale(1); }
  }

  // ── Focus ──
  &__input:focus-visible + &__bullet {
    box-shadow: 0 0 0 3px rgba(67, 146, 212, 0.20);
  }

  // ── Disabled ──
  &__input:disabled + &__bullet {
    background: $ar-bg-soft;
    border-color: $ar-border-soft;
  }

  // ── Size variants ──
  &--sm {
    .app-radio__bullet { width: 14px; height: 14px; }
    .app-radio__bullet::after { width: 6px; height: 6px; }
    .app-radio__label { font-size: 12px; }
  }
}
</style>
